import type {
    SimulatorAppliedFilters,
    SimulatorCategoryApplyFilter,
    SimulatorCategoryAttributeCache,
    SimulatorFilterCategory,
    SimulatorFilterOption,
} from './SimulatorFilterPanel.types';
import {
    type SimulatorFilterScopeItem,
    type SimulatorShowOnlyState,
} from './simulatorFilter.helpers';
import { fetchAllSimulatorFilterAttributeNames } from './useSimulatorFilterAttributeNames';
import { isTrustedPrecomputedCategoryApplyFilter } from './simulatorFilterExcludeInclude.helpers';

function resolveCategoryAllOptionIds(
    cached: SimulatorCategoryAttributeCache | undefined,
): string[] | undefined {
    if (cached?.hasAllOptions && cached.allOptionIds?.length) {
        return cached.allOptionIds;
    }
    return undefined;
}

function wasCategorySelectAllUsed(filters: SimulatorAppliedFilters, categoryKey: string): boolean {
    return Boolean(filters.categoryUsedSelectAll?.[categoryKey]);
}

function areSimulatorShowOnlyStatesEqual(
    left: SimulatorShowOnlyState,
    right: SimulatorShowOnlyState,
): boolean {
    return (
        left.edited === right.edited &&
        left.impacted === right.impacted &&
        JSON.stringify(left.editedFilters) === JSON.stringify(right.editedFilters)
    );
}

function isSameSimulatorCategoryFilterState(
    previous: SimulatorAppliedFilters,
    next: SimulatorAppliedFilters,
    categoryKey: string,
): boolean {
    const previousSelectAll = Boolean(previous.categorySelectAll?.[categoryKey]);
    const nextSelectAll = Boolean(next.categorySelectAll?.[categoryKey]);
    if (previousSelectAll !== nextSelectAll) {
        return false;
    }

    const previousUsedSelectAll = wasCategorySelectAllUsed(previous, categoryKey);
    const nextUsedSelectAll = wasCategorySelectAllUsed(next, categoryKey);
    if (previousUsedSelectAll !== nextUsedSelectAll) {
        return false;
    }

    const previousIds = previous.categorySelections[categoryKey] ?? [];
    const nextIds = next.categorySelections[categoryKey] ?? [];
    if (previousIds.length !== nextIds.length) {
        return false;
    }

    const previousIdSet = new Set(previousIds);
    return nextIds.every((id) => previousIdSet.has(id));
}

function dropStaleCategoryApplyMetadata(
    previous: SimulatorAppliedFilters,
    next: SimulatorAppliedFilters,
    mergedApplyFilters: Record<string, SimulatorCategoryApplyFilter>,
    mergedPreviewOptions: Record<string, SimulatorFilterOption[]>,
    mergedCache: Record<string, SimulatorCategoryAttributeCache>,
) {
    Object.keys(mergedApplyFilters).forEach((categoryKey) => {
        if (!isSameSimulatorCategoryFilterState(previous, next, categoryKey)) {
            delete mergedApplyFilters[categoryKey];
            delete mergedPreviewOptions[categoryKey];
            if (mergedCache[categoryKey]) {
                mergedCache[categoryKey] = { totalCount: mergedCache[categoryKey].totalCount };
            }
        }
    });
}

function buildMergedCategorySelectAll(
    previous: SimulatorAppliedFilters,
    next: SimulatorAppliedFilters,
): Record<string, boolean> | undefined {
    const hasSelectAll =
        Object.keys(next.categorySelectAll ?? {}).length > 0 || Boolean(previous.categorySelectAll);
    if (!hasSelectAll) {
        return undefined;
    }
    return { ...(previous.categorySelectAll ?? {}), ...(next.categorySelectAll ?? {}) };
}

function buildMergedCategoryUsedSelectAll(
    previous: SimulatorAppliedFilters,
    next: SimulatorAppliedFilters,
): Record<string, boolean> | undefined {
    const hasUsedSelectAll =
        Object.keys(next.categoryUsedSelectAll ?? {}).length > 0 ||
        Boolean(previous.categoryUsedSelectAll);
    if (!hasUsedSelectAll) {
        return undefined;
    }
    return { ...(previous.categoryUsedSelectAll ?? {}), ...(next.categoryUsedSelectAll ?? {}) };
}

function appendMergedOptionalFilterFields(
    merged: SimulatorAppliedFilters,
    fields: {
        categorySelectAll?: Record<string, boolean>;
        categoryUsedSelectAll?: Record<string, boolean>;
        categoryApplyFilters?: Record<string, SimulatorCategoryApplyFilter>;
        categorySelectAllPreviewOptions?: Record<string, SimulatorFilterOption[]>;
        categoryAttributeCache?: Record<string, SimulatorCategoryAttributeCache>;
    },
): SimulatorAppliedFilters {
    return {
        ...merged,
        ...(fields.categorySelectAll ? { categorySelectAll: fields.categorySelectAll } : {}),
        ...(fields.categoryUsedSelectAll ? { categoryUsedSelectAll: fields.categoryUsedSelectAll } : {}),
        ...(fields.categoryApplyFilters ? { categoryApplyFilters: fields.categoryApplyFilters } : {}),
        ...(fields.categorySelectAllPreviewOptions
            ? { categorySelectAllPreviewOptions: fields.categorySelectAllPreviewOptions }
            : {}),
        ...(fields.categoryAttributeCache ? { categoryAttributeCache: fields.categoryAttributeCache } : {}),
    };
}

/** Keeps apply metadata for unchanged categories when applying additional filters from the panel. */
export function mergeSimulatorAppliedFiltersOnPanelApply(
    previous: SimulatorAppliedFilters,
    next: SimulatorAppliedFilters,
): SimulatorAppliedFilters {
    const showOnlyUnchanged = areSimulatorShowOnlyStatesEqual(previous.showOnly, next.showOnly);
    const mergedPreviewOptions = {
        ...(previous.categorySelectAllPreviewOptions ?? {}),
        ...(next.categorySelectAllPreviewOptions ?? {}),
    };
    const mergedCache: Record<string, SimulatorCategoryAttributeCache> = {
        ...(previous.categoryAttributeCache ?? {}),
        ...(next.categoryAttributeCache ?? {}),
    };
    const mergedApplyFilters: Record<string, SimulatorCategoryApplyFilter> = showOnlyUnchanged
        ? { ...(previous.categoryApplyFilters ?? {}) }
        : {};

    if (showOnlyUnchanged) {
        dropStaleCategoryApplyMetadata(previous, next, mergedApplyFilters, mergedPreviewOptions, mergedCache);
    }

    const nextApplyFilters = next.categoryApplyFilters ?? next.categoryDraftApplyFilters;
    if (nextApplyFilters) {
        Object.assign(mergedApplyFilters, nextApplyFilters);
    }

    const baseMerged: SimulatorAppliedFilters = {
        categorySelections: next.categorySelections,
        showOnly: next.showOnly,
        categoryTotalCounts: {
            ...(previous.categoryTotalCounts ?? {}),
            ...(next.categoryTotalCounts ?? {}),
        },
    };

    return appendMergedOptionalFilterFields(baseMerged, {
        categorySelectAll: buildMergedCategorySelectAll(previous, next),
        categoryUsedSelectAll: buildMergedCategoryUsedSelectAll(previous, next),
        categoryApplyFilters:
            Object.keys(mergedApplyFilters).length > 0 ? mergedApplyFilters : undefined,
        categorySelectAllPreviewOptions:
            Object.keys(mergedPreviewOptions).length > 0 ? mergedPreviewOptions : undefined,
        categoryAttributeCache: Object.keys(mergedCache).length > 0 ? mergedCache : undefined,
    });
}

function hasPrecomputedCategoryApplyFilter(
    applyFilter: SimulatorCategoryApplyFilter | undefined,
): applyFilter is SimulatorCategoryApplyFilter {
    if (!applyFilter) {
        return false;
    }
    if (applyFilter.inclusion !== undefined) {
        return true;
    }
    return (applyFilter.exclusion?.length ?? 0) > 0;
}

function isActiveSimulatorFilterCategory(
    filters: SimulatorAppliedFilters,
    categoryKey: string,
): boolean {
    if (hasPrecomputedCategoryApplyFilter(filters.categoryApplyFilters?.[categoryKey])) {
        return true;
    }

    const selectedCount = filters.categorySelections[categoryKey]?.length ?? 0;
    const isSelectAll = Boolean(filters.categorySelectAll?.[categoryKey]);
    const usedSelectAll = wasCategorySelectAllUsed(filters, categoryKey);
    return selectedCount > 0 || isSelectAll || usedSelectAll;
}

/** True when every active category already has inclusion/exclusion lists (e.g. restored from persist_filter). */
export function hasCompleteCategoryApplyMetadata(
    filters: SimulatorAppliedFilters,
    filterCategories: SimulatorFilterCategory[],
): boolean {
    const activeCategories = filterCategories.filter((category) =>
        isActiveSimulatorFilterCategory(filters, category.key),
    );

    if (activeCategories.length === 0) {
        return true;
    }

    return activeCategories.every((category) =>
        hasPrecomputedCategoryApplyFilter(filters.categoryApplyFilters?.[category.key]),
    );
}

function copyPrecomputedCategoryApplyFilter(
    precomputed: SimulatorCategoryApplyFilter,
    totalCount: number | undefined,
    selectedCount: number,
): { applyFilter: SimulatorCategoryApplyFilter; totalCount: number } {
    if (precomputed.inclusion !== undefined) {
        return {
            applyFilter: { inclusion: [...precomputed.inclusion] },
            totalCount: totalCount ?? selectedCount,
        };
    }
    return {
        applyFilter: { exclusion: [...(precomputed.exclusion ?? [])] },
        totalCount: totalCount ?? selectedCount,
    };
}

async function resolveExclusionCategoryApplyFilter(
    category: SimulatorFilterCategory,
    selectedSet: Set<string>,
    totalCount: number,
    cached: SimulatorCategoryAttributeCache | undefined,
    filters: SimulatorAppliedFilters,
    appliedScope?: SimulatorFilterScopeItem[],
): Promise<{ applyFilter: SimulatorCategoryApplyFilter; totalCount: number }> {
    let allIds = resolveCategoryAllOptionIds(cached);
    if (!allIds || allIds.length < totalCount) {
        const allOptions = await fetchAllSimulatorFilterAttributeNames({
            category,
            appliedScope,
            showOnly: filters.showOnly,
        });
        allIds = allOptions.map((option) => option.id);
    }

    return {
        applyFilter: { exclusion: allIds.filter((id) => !selectedSet.has(id)) },
        totalCount,
    };
}

/** GraphQL applyFilter payload — inclusion vs exclusion is resolved here only. */
async function resolveCategoryApplyFilter(
    category: SimulatorFilterCategory,
    filters: SimulatorAppliedFilters,
    resolvedCache: Record<string, SimulatorCategoryAttributeCache> | undefined,
    appliedScope?: SimulatorFilterScopeItem[],
): Promise<{ applyFilter: SimulatorCategoryApplyFilter; totalCount: number }> {
    const selectedIds = filters.categorySelections[category.key] ?? [];
    const isSelectAll = Boolean(filters.categorySelectAll?.[category.key]);
    const usedSelectAll = wasCategorySelectAllUsed(filters, category.key);
    const selectedSet = new Set(selectedIds);
    const selectedCount = selectedIds.length;
    const cached = resolvedCache?.[category.key];
    const totalCount = cached?.totalCount ?? filters.categoryTotalCounts?.[category.key];
    const precomputedApplyFilter = filters.categoryApplyFilters?.[category.key];

    if (
        hasPrecomputedCategoryApplyFilter(precomputedApplyFilter) &&
        isTrustedPrecomputedCategoryApplyFilter(precomputedApplyFilter, selectedIds, {
            isSelectAll,
            usedSelectAll,
        })
    ) {
        return copyPrecomputedCategoryApplyFilter(precomputedApplyFilter, totalCount, selectedCount);
    }

    // Select All — no unticks (global filter `{}`).
    if ((isSelectAll || usedSelectAll) && selectedCount === 0) {
        return { applyFilter: { inclusion: [] }, totalCount: totalCount ?? 0 };
    }

    // Select All + unticks — fallback when draft apply metadata is missing.
    if (usedSelectAll && selectedCount > 0) {
        return resolveExclusionCategoryApplyFilter(
            category,
            selectedSet,
            totalCount ?? selectedCount,
            cached,
            filters,
            appliedScope,
        );
    }

    // Manual pick — always inclusion.
    return {
        applyFilter: { inclusion: [...selectedIds] },
        totalCount: totalCount ?? selectedCount,
    };
}

/** Builds inclusion/exclusion apply lists for GraphQL applyFilter / tab-switch table fetch. */
export async function enrichAppliedFiltersWithCategoryApplyMetadata(
    filters: SimulatorAppliedFilters,
    filterCategories: SimulatorFilterCategory[],
    appliedScope?: SimulatorFilterScopeItem[],
    categoryAttributeCache?: Record<string, SimulatorCategoryAttributeCache>,
): Promise<SimulatorAppliedFilters> {
    const categoriesWithSelections = filterCategories.filter((category) =>
        isActiveSimulatorFilterCategory(filters, category.key),
    );

    if (categoriesWithSelections.length === 0) {
        const { categoryAttributeCache: _cache, categoryUsedSelectAll: _used, ...rest } = filters;
        return {
            ...rest,
            categoryTotalCounts: undefined,
            categoryApplyFilters: undefined,
        };
    }

    const resolvedCache = categoryAttributeCache ?? filters.categoryAttributeCache;
    const categoryApplyFilters: Record<string, SimulatorCategoryApplyFilter> = {};
    const categoryTotalCounts: Record<string, number> = {};

    await Promise.all(
        categoriesWithSelections.map(async (category) => {
            const { applyFilter, totalCount } = await resolveCategoryApplyFilter(
                category,
                filters,
                resolvedCache,
                appliedScope,
            );
            categoryApplyFilters[category.key] = applyFilter;
            categoryTotalCounts[category.key] = totalCount;
        }),
    );

    const { categoryAttributeCache: _cache, ...rest } = filters;

    return {
        ...rest,
        categoryTotalCounts,
        categoryApplyFilters,
    };
}
